import {
	AncilliariesRecommendationsList,
	CardWrapper,
	ImageSection,
} from '../../recommendations/ancilliaries-recommendations-caroussel';
import { HighlightedRecommendationCard } from '../../recommendations/highlighted-recommendation-card';
import { HighlightedRecommendation, TopFiveRecommendations } from '../../types';
import { cn } from '@fshr/shared-react';
import { ArrowRight } from '../../icon/arrow-right';
import { useTranslations } from '@fshr/context/i18n-context';
import BevSafeText from '../../bev-safe-text';

const AncilliariesRecommendationVerticalList = ({
	ancilliariesRecommendations,
	placement,
}: {
	ancilliariesRecommendations: TopFiveRecommendations[];
	placement?: string;
}) => {
	return (
		<div className="flex flex-col gap-[15px]">
			{ancilliariesRecommendations.map(
				({ image, title, price, propertyOrsCode, sku, url, status }) => (
					<CardWrapper
						url={url}
						propertyOrsCode={propertyOrsCode}
						sku={sku}
						key={sku}
						containerClassName="w-full flex-row gap-[15px]!"
					>
						<div className="w-[120px] h-[120px] min-w-[120px] min-h-[120px] flex-shrink-0">
							<ImageSection
								altText={image.altText}
								image={image.url}
								// text={status?.message}
								// color={status?.color}
								imageContainerClassName="w-full h-full min-w-full min-h-full flex-shrink-0"
							/>
						</div>
						<div className="w-[calc(100%-120px)] flex flex-row justify-between">
							<div className="w-full flex flex-col spacing-stack-2xs text-subtitle2">
								<div className="w-full flex flex-row justify-between spacing-inline-xs">
									<h3 className="line-clamp-2 text-text-primary">
										<BevSafeText>{title}</BevSafeText>
									</h3>
									<ArrowRight />
								</div>
								<div className="flex flex-row flex-wrap spacing-inline-2xs">
									<p className="text-text-secondary">{price?.total}</p>
									<p className="text-caption">
										<span className="text-text-secondary">{price?.type ? `/person` : ''}</span>
									</p>
								</div>
							</div>
						</div>
					</CardWrapper>
				)
			)}
		</div>
	);
};

export const AncilliariesRecommendationsComponent = ({
	ancilliariesRecommendations,
	isLoading,
	highlightedRecommendation,
	trackRecoImpressions = true,
	title,
	minRecommendationsToShow = 1,
	maxRecommendationsToShow = 5,
	showHighlightedRecommendation = true,
}: {
	ancilliariesRecommendations: TopFiveRecommendations[];
	highlightedRecommendation?: HighlightedRecommendation;
	isLoading?: boolean;
	trackRecoImpressions?: boolean;
	title?: string;
	minRecommendationsToShow?: number;
	maxRecommendationsToShow?: number;
	showHighlightedRecommendation?: boolean;
}) => {
	const t = useTranslations();
	if (isLoading) {
		return (
			<div className="w-full px-[10px]">
				<div
					className={cn(
						'w-full animate-pulse border-0 bg-neutral-200 h-[140px] relative overflow-hidden'
					)}
				/>
			</div>
		);
	}

	const visibleRecommendations = ancilliariesRecommendations.slice(0, maxRecommendationsToShow);
	const recoCount = visibleRecommendations.length;
	if (recoCount < minRecommendationsToShow && !highlightedRecommendation) {
		return null;
	}

	return (
		<div className="w-full flex flex-col py-[20px] px-[30px] gap-[15px] bg-surface-raised-subtle">
			<h2 className="text-subtitle2">
				<span className="text-text-secondary">{title ?? t('Recommended for you')}</span>
			</h2>
			{showHighlightedRecommendation && highlightedRecommendation ? (
				<HighlightedRecommendationCard
					recommendation={highlightedRecommendation}
					placement="shopping_cart-highlighted_ancillary"
					position={1}
					trackRecoImpressions={trackRecoImpressions}
				/>
			) : null}
			{recoCount >= Math.max(3, minRecommendationsToShow) ? (
				<AncilliariesRecommendationsList
					gap={15}
					ancilliariesRecommendations={visibleRecommendations}
					placement="shopping_cart-recommended_ancillaries"
					trackRecoImpressions={trackRecoImpressions}
				/>
			) : recoCount >= minRecommendationsToShow ? (
				<AncilliariesRecommendationVerticalList
					ancilliariesRecommendations={visibleRecommendations}
					placement="shopping_cart-recommended_ancillaries"
				/>
			) : null}
		</div>
	);
};
